// AI Post Generator Component
import React, { useState } from 'react';
import { Sparkles, Loader2, RefreshCw, Lightbulb } from 'lucide-react';
import { PostGenerationRequest, SocialPlatform } from '../types/social.types';
import { ClaudePostGenerator, postTemplates } from '../services/claudePostGenerator';

interface Props {
  onGenerate: (content: string, platforms: SocialPlatform[]) => void;
}

const AIPostGenerator: React.FC<Props> = ({ onGenerate }) => {
  const [topic, setTopic] = useState('');
  const [tone, setTone] = useState<PostGenerationRequest['tone']>('professional');
  const [length, setLength] = useState<PostGenerationRequest['length']>('medium');
  const [platforms, setPlatforms] = useState<SocialPlatform[]>(['facebook']);
  const [includeHashtags, setIncludeHashtags] = useState(true);
  const [includeEmojis, setIncludeEmojis] = useState(true);
  const [productMention, setProductMention] = useState('');
  const [callToAction, setCallToAction] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedContent, setGeneratedContent] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const [contentIdeas, setContentIdeas] = useState<string[]>([]);

  const generator = new ClaudePostGenerator(import.meta.env.VITE_CLAUDE_API_KEY || '');

  const handleGenerate = async () => {
    if (!topic) {
      alert('Podaj temat posta');
      return;
    }

    setIsGenerating(true);
    try {
      const request: PostGenerationRequest = {
        topic,
        tone,
        length,
        platforms,
        includeHashtags,
        includeEmojis,
        productMention: productMention || undefined,
        callToAction: callToAction || undefined
      };

      const result = await generator.generatePost(request);
      setGeneratedContent(result.content);
      
      // Pokaż warianty dla różnych platform
      if (result.variants.length > 0) {
        console.log('Platform variants:', result.variants);
      }
    } catch (error) {
      console.error('Error generating post:', error);
      alert('Błąd podczas generowania posta');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleGetIdeas = async () => {
    if (!topic) {
      alert('Podaj temat, aby otrzymać pomysły');
      return;
    }

    try {
      const ideas = await generator.generateContentIdeas(topic, 5);
      setContentIdeas(ideas);
    } catch (error) {
      console.error('Error getting ideas:', error);
    }
  };

  const handleUseTemplate = (templateKey: string) => {
    const template = postTemplates[templateKey as keyof typeof postTemplates];
    if (template) {
      setSelectedTemplate(templateKey);
      // Możesz tutaj ustawić domyślne wartości dla szablonu
    }
  };

  const handleAccept = () => {
    if (generatedContent) {
      onGenerate(generatedContent, platforms);
    }
  };

  return (
    <div className="space-y-6">
      {/* Szablony */}
      <div>
        <h3 className="text-sm font-medium text-gray-300 mb-3">
          Wybierz szablon (opcjonalne)
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {Object.entries(postTemplates).map(([key, template]) => (
            <button
              key={key}
              onClick={() => handleUseTemplate(key)}
              className={`p-3 text-left rounded-lg border-2 transition-all ${
                selectedTemplate === key
                  ? 'border-purple-500 bg-purple-600/20 text-purple-400'
                  : 'border-zinc-600 hover:border-zinc-500 text-gray-400 hover:text-white'
              }`}
            >
              <p className="font-medium text-sm">{template.title}</p>
              <p className="text-xs text-gray-500 mt-1">
                {template.variables.length} pól do uzupełnienia
              </p>
            </button>
          ))}
        </div>
      </div>

      {/* Główny formularz */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Lewa kolumna */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Temat posta *
            </label>
            <div className="relative">
              <input
                type="text"
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                placeholder="np. Nowa kolekcja ekspozytorów z plexi"
                className="w-full p-3 pr-10 bg-zinc-900 border border-zinc-700 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-white placeholder-gray-500"
              />
              <button
                onClick={handleGetIdeas}
                className="absolute right-2 top-3 text-gray-500 hover:text-gray-300"
                title="Pokaż pomysły"
              >
                <Lightbulb className="w-5 h-5" />
              </button>
            </div>
            
            {/* Pomysły na treść */}
            {contentIdeas.length > 0 && (
              <div className="mt-2 p-3 bg-purple-900/20 border border-purple-800 rounded-lg">
                <p className="text-xs font-medium text-purple-400 mb-2">
                  Pomysły na posty:
                </p>
                <div className="space-y-1">
                  {contentIdeas.map((idea, index) => (
                    <button
                      key={index}
                      onClick={() => setTopic(idea)}
                      className="block w-full text-left text-sm text-purple-300 hover:text-purple-200"
                    >
                      • {idea}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Ton wypowiedzi
            </label>
            <select
              value={tone}
              onChange={(e) => setTone(e.target.value as PostGenerationRequest['tone'])}
              className="w-full p-3 bg-zinc-900 border border-zinc-700 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-white"
            >
              <option value="professional">Profesjonalny</option>
              <option value="casual">Swobodny</option>
              <option value="enthusiastic">Entuzjastyczny</option>
              <option value="informative">Informacyjny</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Długość posta
            </label>
            <select
              value={length}
              onChange={(e) => setLength(e.target.value as PostGenerationRequest['length'])}
              className="w-full p-3 bg-zinc-900 border border-zinc-700 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-white"
            >
              <option value="short">Krótki (do 280 znaków)</option>
              <option value="medium">Średni (280-500 znaków)</option>
              <option value="long">Długi (500-1000 znaków)</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Wspomnij o produkcie (opcjonalne)
            </label>
            <input
              type="text"
              value={productMention}
              onChange={(e) => setProductMention(e.target.value)}
              placeholder="np. Ekspozytor A4 z plexi"
              className="w-full p-3 bg-zinc-900 border border-zinc-700 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-white placeholder-gray-500"
            />
          </div>
        </div>

        {/* Prawa kolumna */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Platformy docelowe
            </label>
            <div className="space-y-2">
              {(['facebook', 'linkedin', 'instagram', 'twitter'] as SocialPlatform[]).map(platform => (
                <label key={platform} className="flex items-center text-gray-300">
                  <input
                    type="checkbox"
                    checked={platforms.includes(platform)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setPlatforms([...platforms, platform]);
                      } else {
                        setPlatforms(platforms.filter(p => p !== platform));
                      }
                    }}
                    className="mr-2 rounded text-purple-600 bg-zinc-700 border-zinc-600"
                  />
                  <span className="capitalize">{platform}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Wezwanie do działania (opcjonalne)
            </label>
            <input
              type="text"
              value={callToAction}
              onChange={(e) => setCallToAction(e.target.value)}
              placeholder="np. Sprawdź naszą ofertę!"
              className="w-full p-3 bg-zinc-900 border border-zinc-700 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-white placeholder-gray-500"
            />
          </div>

          <div className="space-y-2">
            <label className="flex items-center text-gray-300">
              <input
                type="checkbox"
                checked={includeHashtags}
                onChange={(e) => setIncludeHashtags(e.target.checked)}
                className="mr-2 rounded text-purple-600 bg-zinc-700 border-zinc-600"
              />
              <span className="text-sm">Dodaj hashtagi</span>
            </label>
            <label className="flex items-center text-gray-300">
              <input
                type="checkbox"
                checked={includeEmojis}
                onChange={(e) => setIncludeEmojis(e.target.checked)}
                className="mr-2 rounded text-purple-600 bg-zinc-700 border-zinc-600"
              />
              <span className="text-sm">Użyj emoji</span>
            </label>
          </div>
        </div>
      </div>

      {/* Przycisk generowania */}
      <div className="flex justify-center">
        <button
          onClick={handleGenerate}
          disabled={!topic || platforms.length === 0 || isGenerating}
          className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center transition-all"
        >
          {isGenerating ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Generowanie...
            </>
          ) : (
            <>
              <Sparkles className="w-5 h-5 mr-2" />
              Wygeneruj post
            </>
          )}
        </button>
      </div>

      {/* Wygenerowana treść */}
      {generatedContent && (
        <div className="mt-6 p-6 bg-purple-900/20 border border-purple-800 rounded-lg">
          <div className="flex justify-between items-start mb-4">
            <h3 className="font-medium text-purple-400">
              Wygenerowany post
            </h3>
            <button
              onClick={handleGenerate}
              className="text-purple-400 hover:text-purple-300"
              title="Wygeneruj ponownie"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
          </div>
          
          <div className="bg-zinc-900 border border-zinc-700 p-4 rounded-lg mb-4">
            <p className="whitespace-pre-wrap text-white">{generatedContent}</p>
          </div>

          <div className="flex justify-end gap-3">
            <button
              onClick={() => setGeneratedContent('')}
              className="px-4 py-2 text-gray-400 hover:text-gray-300 transition-all"
            >
              Anuluj
            </button>
            <button
              onClick={handleAccept}
              className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-all"
            >
              Użyj tej treści
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIPostGenerator;
